import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import {
  isLeafTypePrimitive,
  LEAF_TYPE_TO_CHILDREN,
  LeafType,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {FIELD_TYPE_TO_LEAF_TYPE, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';

function getAllowedLeafTypes(fieldType: FieldType): Array<LeafType> {
  // Currently, mapping to JSON fields is not supported.
  if (fieldType === FieldType.JSON) {
    return [];
  }

  const exactMatchLeafType = FIELD_TYPE_TO_LEAF_TYPE[fieldType];

  // Any column may be written to TEXT
  if (exactMatchLeafType === LeafType.TEXT) {
    return Object.values(LeafType);
  }

  // TEXT and JSON columns may contain values parseable to any field type.
  let allowedLeafTypes = [exactMatchLeafType, LeafType.TEXT, LeafType.JSON];

  for (const [parentType, childTypes] of Object.entries(LEAF_TYPE_TO_CHILDREN)) {
    if (exactMatchLeafType === parentType) {
      allowedLeafTypes = [...allowedLeafTypes, ...childTypes];
    } else if (childTypes.includes(exactMatchLeafType)) {
      allowedLeafTypes = [...allowedLeafTypes, parentType as LeafType];
    }
  }

  if (!isLeafTypePrimitive(exactMatchLeafType)) {
    allowedLeafTypes = [...allowedLeafTypes, LeafType.BENCHLING_OBJECT];
  }

  // DECIMAL and INTEGER fields may contain values coercible to BOOLEAN.
  if (exactMatchLeafType === LeafType.BOOLEAN) {
    allowedLeafTypes = [...allowedLeafTypes, LeafType.DECIMAL, LeafType.INTEGER];
  }

  return allowedLeafTypes;
}

/**
 * Filter data frame columns to those that may be mapped to a schema field.
 *
 * This function only validates that the mapping might succeed based on the column and field types; it does
 * not check the row values or guarantee the mapping will succeed.
 *
 * For example, mapping to a FLOAT field, a TEXT column will be included, as it may contain strings that
 * can be parsed as floats.
 *
 * Only columns' leaf types are considered, because:
 * - A non-List column can be mapped to a Multi-select field
 * - A List column can be mapped to a non-Multi-select field, if each list contains one item
 * - A Nullable column can be mapped to a Required field, if there are no nulls
 * - A MaybeText column can be mapped, if there are no special text values
 *
 * @param columnDefs Source column definitions
 * @param field Destination field
 */
export default function getColumnsThatMayBeMappedToField(
  columnDefs: DataFrameColumns,
  field: SchemaField
): DataFrameColumns {
  const allowedLeafTypes = getAllowedLeafTypes(field.fieldType);
  return columnDefs.filter(
    (columnDef) => allowedLeafTypes.includes(columnDef.type.leafType) && !isSystemColumn(columnDef)
  );
}
